import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { AgentConfig } from "@/types";

function useAgentConfigClient() {
  const { session, logout } = useAuth();
  return () =>
    createApiClient({
      getToken: () => session?.accessToken ?? null,
      onUnauthorized: logout,
    });
}

interface MutationVars {
  agentId: string;
  payload: Record<string, unknown>;
}

// PUT managed — replace komplette Ebene (destruktiv, admin-only).
export function useReplaceManaged(): UseMutationResult<AgentConfig, Error, MutationVars> {
  const qc = useQueryClient();
  const getClient = useAgentConfigClient();
  return useMutation({
    mutationFn: ({ agentId, payload }) =>
      getClient().put<AgentConfig>(`/agents/${agentId}/config/managed`, { managed: payload }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.detail("agents", vars.agentId) });
    },
  });
}

// PATCH managed — merge (admin-only).
export function usePatchManaged(): UseMutationResult<AgentConfig, Error, MutationVars> {
  const qc = useQueryClient();
  const getClient = useAgentConfigClient();
  return useMutation({
    mutationFn: ({ agentId, payload }) =>
      getClient().patch<AgentConfig>(`/agents/${agentId}/config/managed`, { payload }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.detail("agents", vars.agentId) });
    },
  });
}

// PATCH owner — merge (owner-self oder admin).
export function usePatchOwner(): UseMutationResult<AgentConfig, Error, MutationVars> {
  const qc = useQueryClient();
  const getClient = useAgentConfigClient();
  return useMutation({
    mutationFn: ({ agentId, payload }) =>
      getClient().patch<AgentConfig>(`/agents/${agentId}/config/owner`, { payload }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.detail("agents", vars.agentId) });
    },
  });
}

// PATCH mcpServers — merge (owner-self oder admin).
export function usePatchMcpServers(): UseMutationResult<AgentConfig, Error, MutationVars> {
  const qc = useQueryClient();
  const getClient = useAgentConfigClient();
  return useMutation({
    mutationFn: ({ agentId, payload }) =>
      getClient().patch<AgentConfig>(`/agents/${agentId}/config/mcpServers`, { payload }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.detail("agents", vars.agentId) });
    },
  });
}

